import React, { ComponentProps, useEffect, useRef, useState } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List, ListRowProps } from 'react-virtualized';
import { Box } from '@mui/material';
const DefaultItemHeight = 32;
const DefaultSeparatorHeight = 16;

export default function ReactSelectVirtualMenuList(props: any) {
    const { children, maxHeight } = props;
    const rows = children;
    const listRef = useRef<List>(null);
    const cache = new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: DefaultItemHeight,
        keyMapper: (rowIndex) => {
            const item = rows[rowIndex];
            return item ? item.props.data.label : rowIndex;
        },
    });
    const CellMeasurerAny = CellMeasurer as any;
    const RowComponent = React.forwardRef<HTMLDivElement, ComponentProps<any>>((props, ref) => {
        return (
            <Box sx={{ '&  *': { wordWrap: 'break-word', width: '100%' } }} ref={ref}>
                {props.data}
            </Box>
        );
    });
    const rowRenderer = ({ key, index, style, parent }: ListRowProps) => {
        return (
            <CellMeasurerAny parent={parent} key={key} cache={cache} columnIndex={0} rowIndex={index}>
                {({ measure, registerChild }: any) => (
                    <div style={style}>
                        <div ref={registerChild} className="react-virtualized-list__item">
                            <RowComponent measure={measure} data={rows[index]} />
                        </div>
                    </div>
                )}
            </CellMeasurerAny>
        );
    };
    const [totalWrapperHeight, setTotalWrapperHeight] = useState(
        Array.isArray(rows) ? rows.length * DefaultItemHeight : 0,
    );

    const [resizeKey, setResizeKey] = useState(0);
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const adjustHeight = () => {
        const totalHeight = rows.reduce((sum: number, _: any, index: number) => {
            return sum + cache.getHeight(index, 0);
        }, 0);

        let s = maxHeight;
        if (rows?.length < 10) {
            s = totalHeight;
        }
        if (totalWrapperHeight != s) {
            setTotalWrapperHeight(s);
        }
    };

    const handleResize = () => {
        requestAnimationFrame(() => {
            // Incrementing resizeKey to forces List to remount and re-measure
            setResizeKey((k) => k + 1);
        });
    };

    if (!Array.isArray(rows)) {
        return <>{children}</>;
    }

    return (
        <div style={{ height: totalWrapperHeight, maxHeight: maxHeight }}>
            {/*// @ts-ignore*/}
            <AutoSizer
                onResize={() => {
                    cache.clearAll();
                    requestAnimationFrame(() => {
                        listRef.current?.recomputeRowHeights();
                    });
                }}
            >
                {({ width, height }) => {
                    return (
                        // @ts-ignore
                        <List
                            key={resizeKey}
                            style={{ scrollbarGutter: totalWrapperHeight > maxHeight ? 'stable' : undefined }}
                            ref={listRef}
                            width={width}
                            height={height}
                            rowCount={rows.length}
                            deferredMeasurementCache={cache}
                            rowHeight={({ index }) => {
                                return rows[index].props?.data?.value == 'sep'
                                    ? DefaultSeparatorHeight
                                    : cache.rowHeight({ index });
                            }}
                            onRowsRendered={() =>
                                requestAnimationFrame(() => {
                                    adjustHeight();
                                })
                            }
                            rowRenderer={rowRenderer}
                        />
                    );
                }}
            </AutoSizer>
        </div>
    );
}
